/**
 *  Note: adding imports to this file could cause issues with unit tests -
 *  (i.e. imports not being available during test rendering)
 **/
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { logger } from '../features/logger';
import { logoutAndRedirectToLogin } from './auth/logout';
import { BRACKET_REGEX } from './constants';
import dynamicRefs from './dynamicRefHelper';
import { getFieldNameFromErrorObject } from './refHelper';
export async function apiResponseHandler(response, oktaAuth) {
  const status = response.status;
  if (status === 401 || status === 403) {
    // redirect to login if token invalid or expired
    logoutAndRedirectToLogin(oktaAuth);
    return <Redirect to="/login" />;
  }

  const text = await response.text();
  let resp;
  try {
    resp = JSON.parse(text);
  } catch (e) {
    logger.error('Failed to parse JSON response', e);
    // no json response
    resp = { message: text };
  }

  return resp;
}

export function timeoutPromise(ms, promise) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Promise Timeout'));
    }, ms);
    promise.then(
      res => {
        clearTimeout(timeoutId);
        resolve(res);
      },
      err => {
        clearTimeout(timeoutId);
        reject(err);
      }
    );
  });
}

export function getArrayItemFromName(store, name, match) {
  const names = name.split('.');
  const parentWithBracket = names.shift();
  const parent = parentWithBracket.split(BRACKET_REGEX)[0];
  return store.customer[parent][match];
}

export function getAddressString(address) {
  return `${address.street || '__'} ${address.city || '__'}, ${address.state || '__'}`;
}

export function getShipToSubtitle({ shipToAddress, store }) {
  let addressString;

  if (shipToAddress.useBillTo) {
    const { billTo = {} } = store.customer;
    const billToAddress = billTo.address;
    addressString = billToAddress ? getAddressString(billToAddress) : '';
  } else if (shipToAddress.usePayer) {
    const { payer = {} } = store.customer;
    const payerAddress = payer.address;
    addressString = payerAddress ? getAddressString(payerAddress) : '';
  } else if (shipToAddress.useSoldTo) {
    const { soldTo = {} } = store.customer;
    const soldToAddress = soldTo.address;
    addressString = soldToAddress ? getAddressString(soldToAddress) : '';
  } else {
    addressString = getAddressString(shipToAddress);
  }

  return addressString ? addressString : '';
}

export const EMAIL_REGEX =
  /(([^<>()[\]\\.,/;:\s@"]+(\.[^<>()[\]\\.,;/:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
function navigateToInvalidField(errorField, errorRef, headerHeight) {
  if (errorField && errorField.match(/PhoneNumber/)) {
    // The ref we receive from the FieldNumberFormat component does not use useRef and therefor does not have a .current
    errorRef.focus();
  } else {
    if (errorRef && errorRef.current) {
      // else {
      // If the ref is attached to a div, scroll to it
      const el = errorRef.current.offsetTop - headerHeight;
      window.scrollTo({ behavior: 'smooth', top: el });
      // }
      // If the ref is not attached to a div we can focus on it
      // if (errorRef.current.nodeName !== 'DIV' && errorRef.current.focus) {
      //   errorRef.current.focus();
      // }
    }
  }
}

export function useInputFocus(validated, fieldError) {
  const [errorsAsString, setErrorsAsString] = useState('');
  const { getRef } = dynamicRefs();
  useEffect(() => {
    if (
      validated &&
      fieldError &&
      Object.keys(fieldError).length > 0 &&
      errorsAsString !== JSON.stringify(fieldError)
    ) {
      setErrorsAsString(JSON.stringify(fieldError));
      const errorField = getFieldNameFromErrorObject(fieldError);
      const errorRef = getRef(errorField);
      const headerRef = getRef('header');
      navigateToInvalidField(errorField, errorRef, headerRef.current.clientHeight);
    }
  }, [errorsAsString, fieldError, getRef, validated]);
}

export function cleanObject(obj) {
  // fromEntries will receive an array of key value pairs and will return that in the form of an object
  return Object.fromEntries(
    // Iterate through object and return a flatMap of cleaned values
    Object.entries(obj).flatMap(([key, value]) => {
      if (String(value) !== '[object Object]') {
        return [[key, value]];
      } else {
        if (Array.isArray(value)) {
          value = value.map(item => cleanObject(item));
        }
        // Recursively clean nested objects
        const cleanObj = cleanObject(value);
        // If there aren't any keys return an empty array which will be removed from the final object
        // Otherwise return the key value pair
        return Object.keys(cleanObj).length > 0 ? [[key, value]] : [];
      }
    })
  );
}
